import React from 'react';
import { Sparkles, Clock, CheckCircle } from 'lucide-react';
import { OnboardingStatusBanner } from './OnboardingStatusBanner';
import { useTenant } from '../../../../hooks/client/tenant/useTenant';

interface MLSStatusBannerProps {
  organization: any;
}

export const MLSStatusBanner: React.FC<MLSStatusBannerProps> = ({ organization }) => {
  // Fetch tenant data to get subscribedMLS from tenants collection
  const { data: tenant, isLoading: isTenantLoading } = useTenant(organization?.id);

  // Show if MLS step has been completed (even before IDX site setup).
  // Accept legacy camelCase `mlsCompleted` from old Firestore onboarding.
  const mlsCompleted =
    organization?.onboardingData?.mls_completed ??
    organization?.onboardingData?.mlsCompleted;
  const hasMlsEntries = organization?.onboardingData?.idx?.mls_entries?.length > 0;
  if (!mlsCompleted && !hasMlsEntries) return null;

  // Don't show banner until tenant data is loaded to prevent flash
  if (isTenantLoading) return null;

  // Get MLS entries from onboarding data (what user submitted)
  const mlsEntries = organization?.onboardingData?.idx?.mls_entries || [];

  // Nothing to approve if no MLS entries were submitted (e.g. mls_completed
  // set with no entries, or all entries removed). Mirrors the no-entries
  // short-circuit in useMLSSubscriptionStatus so the banner doesn't strand.
  if (mlsEntries.length === 0) return null;

  // Get subscribed MLS from tenant collection (approved MLS - boardCode array)
  const subscribedMLS = tenant?.subscribedMLS || [];

  // Check if all MLS are subscribed (mls_name is the boardCode)
  const allMLSSubscribed =
    mlsEntries.length > 0 &&
    mlsEntries.every((entry: any) => subscribedMLS.includes(entry.mls_name));

  // If all MLS are subscribed, don't show banner
  if (allMLSSubscribed) {
    return null;
  }

  return (
    <OnboardingStatusBanner
      icon={Sparkles}
      title="MLS Approval in Progress - ⚠️ Disclaimer: To ensure Remi has access to Property Intelligence and can discuss property details with leads on the phone, we suggest waiting to launch your flows until your MLS approval is complete."
      variant="purple"
    >
      <div className="space-y-2">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {mlsEntries.map((entry: any, index: number) => {
            // Check if this MLS boardCode is in the subscribedMLS array
            const isSubscribed = subscribedMLS.includes(entry.mls_name);
            return (
              <div key={index} className="flex items-center gap-1.5">
                <span className="font-medium">{entry.mls_name || `MLS ${index + 1}`}:</span>
                {isSubscribed ? (
                  <span className="flex items-center gap-1 text-green-700">
                    Approved <CheckCircle className="w-3 h-3" />
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-yellow-700">
                    Approval pending <Clock className="w-3 h-3" />
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </OnboardingStatusBanner>
  );
};
